import {
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  inject,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { InputWrapperComponent } from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import { DeviceItemCardComponent } from '../../../devices/components/device-item-card/device-item-card.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { DeviceItemRowComponent } from '../../../devices/components/device-item-row/device-item-row.component';
import { Store } from '@ngrx/store';
import {
  selectPumpFilter,
  selectPumpsTotalCount,
  selectFilteredPumpsWithCurrentPage,
  selectIsPumpsLoading,
} from '../../state/pump.selectors';
import { PageEvent } from '@angular/material/paginator';
import { catchError, EMPTY, take } from 'rxjs';
import { PumpFilter } from '../../state/pump.state';
import { DeviceSortBy, DeviceSortDir } from '../../../devices/state/device.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { withResetPageOnOtherFilterChange } from '../../../../shared/utils/filter-utils';
import { PumpActions } from '../../state/pump.actions';
import { selectIsSystemRunning } from '../../../dashboard/state/kill-switch.selectors';
import { DeviceResponseDto } from '../../../devices/models/device-response.interface';
import { ViewPreferenceService } from '../../../../core/services/view-preference.service';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { MatDialog } from '@angular/material/dialog';
import { FilterDialogComponent } from '../../../../shared/components/filter-dialog/filter-dialog.component';

type PumpListScope = 'favorite' | 'inService' | 'outOfService';
type PumpSortOrder =
  | 'nameAsc'
  | 'nameDesc'
  | 'lastSeenDateAsc'
  | 'lastSeenDateDesc';

@Component({
  selector: 'app-page-pump-list',
  imports: [
    CardComponent,
    TranslatePipe,
    IconComponent,
    InputWrapperComponent,
    ReactiveFormsModule,
    DeviceItemCardComponent,
    PaginationComponent,
    DeviceItemRowComponent,
    AsyncPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './page-pump-list.component.html',
  styleUrl: './page-pump-list.component.scss',
})
export class PagePumpListComponent implements OnInit {
  private static readonly viewPreferenceKey = 'pumps.page-pump-list';
  private _fb = inject(FormBuilder);
  private store = inject(Store);
  private readonly viewPreferenceService = inject(ViewPreferenceService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  private destroyRef = inject(DestroyRef);

  loading$ = this.store.select(selectIsPumpsLoading);
  totalCount$ = this.store.select(selectPumpsTotalCount);
  pumpList$ = this.store.select(selectFilteredPumpsWithCurrentPage);
  killSwitch$ = this.store.select(selectIsSystemRunning);
  @ViewChild('filterFields', { static: true })
  filterFieldsTemplate?: TemplateRef<unknown>;
  @ViewChild('filterDialogFields', { static: true })
  filterDialogFieldsTemplate?: TemplateRef<unknown>;

  isListView = true;
  isFilterDialogOpen = false;
  filterForm: FormGroup;
  filterOptions = {
    listScope: [
      {
        label: 'devices.filter.listScope.inService',
        value: 'inService' as const,
      },
      {
        label: 'devices.filter.listScope.favorite',
        value: 'favorite' as const,
      },
      {
        label: 'devices.filter.listScope.outOfService',
        value: 'outOfService' as const,
      },
    ],
    sortOrder: [
      { label: 'devices.filter.sort.nameAsc', value: 'nameAsc' as const },
      { label: 'devices.filter.sort.nameDesc', value: 'nameDesc' as const },
      {
        label: 'devices.filter.sort.lastSeenDateDesc',
        value: 'lastSeenDateDesc' as const,
      },
      {
        label: 'devices.filter.sort.lastSeenDateAsc',
        value: 'lastSeenDateAsc' as const,
      },
    ],
  };

  constructor() {
    this.filterForm = this._fb.group({
      searchTerm: [undefined],
      listScope: ['inService' as PumpListScope],
      sortOrder: ['nameAsc' as PumpSortOrder],
      page: [0],
      pageSize: [12],
    });
    this.isListView = this.viewPreferenceService.getIsListView(
      PagePumpListComponent.viewPreferenceKey,
      true,
    );
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  ngOnInit() {
    this.filterForm.valueChanges
      .pipe(
        withResetPageOnOtherFilterChange(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((newFilter) => {
        this.dispatchFilterUpdate(newFilter);
      });

    this.store
      .select(selectPumpFilter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((_) => EMPTY),
      )
      .subscribe((filter) => {
        this.filterForm.patchValue(
          {
            ...filter,
            listScope: this.mapFilterToListScope(filter),
            sortOrder: this.mapFilterToSortOrder(
              filter.sortBy ?? 'name',
              filter.sortDir ?? 'asc',
            ),
          },
          { emitEvent: false },
        );
      });

    queueMicrotask(() => {
      this.dispatchFilterUpdate(this.filterForm.getRawValue());
    });
  }

  onTriggerDeviceAction(evtData: { id: string; value: boolean }) {
    this.store.dispatch(
      PumpActions.public.triggerPumpAction({
        id: evtData.id,
        value: evtData.value,
      }),
    );
  }

  onDeletePump(id: string) {
    this.store.dispatch(PumpActions.public.deletePumpById({ id: id }));
  }

  onEditPump(id: string) {
    this.router.navigate(['/', RouteConstants.routePump, id], {
      queryParams: { edit: true },
    });
  }

  onToggleFavorite(
    device: Pick<DeviceResponseDto, 'id' | 'name' | 'isFavorite'>,
  ) {
    this.store.dispatch(
      PumpActions.public.updatePump({
        id: device.id,
        isFavorite: !device.isFavorite,
      }),
    );
  }

  toggleView() {
    this.isListView = !this.isListView;
    this.viewPreferenceService.setIsListView(
      PagePumpListComponent.viewPreferenceKey,
      this.isListView,
    );
  }

  openFilterDialog() {
    if (!this.filterDialogFieldsTemplate || this.isFilterDialogOpen) return;
    this.isFilterDialogOpen = true;

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: 'min(34rem, calc(100vw - 2rem))',
      maxWidth: '100vw',
      data: {
        titleKey: 'default.filter',
        contentTemplate: this.filterDialogFieldsTemplate,
      },
    });

    this.filterForm.valueChanges
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => dialogRef.close());

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFilterDialogOpen = false;
      });
  }

  private mapFilterToListScope(filter: {
    isFavorite?: boolean;
    isOutOfService?: boolean;
  }): PumpListScope {
    if (filter.isFavorite === true) {
      return 'favorite';
    }

    if (filter.isOutOfService === true) {
      return 'outOfService';
    }

    return 'inService';
  }

  private dispatchFilterUpdate(rawFilter: {
    searchTerm?: string;
    listScope?: PumpListScope;
    sortOrder?: PumpSortOrder;
    page?: number | null;
    pageSize?: number | null;
  }) {
    this.store.dispatch(
      PumpActions.public.updatePumpFilter({
        filter: this.mapFormToPumpFilter(rawFilter),
      }),
    );
  }

  private mapFormToPumpFilter(rawFilter: {
    searchTerm?: string;
    listScope?: PumpListScope;
    sortOrder?: PumpSortOrder;
    page?: number | null;
    pageSize?: number | null;
  }): PumpFilter {
    const { sortBy, sortDir } = this.mapSortOrderToFilterSort(
      rawFilter.sortOrder ?? 'nameAsc',
    );
    const filter: PumpFilter = {
      searchTerm: rawFilter.searchTerm,
      sortBy,
      sortDir,
      page: rawFilter.page ?? undefined,
      pageSize: rawFilter.pageSize ?? undefined,
    };

    if (rawFilter.listScope === 'favorite') {
      filter.isFavorite = true;
    } else if (rawFilter.listScope === 'inService') {
      filter.isOutOfService = false;
    } else if (rawFilter.listScope === 'outOfService') {
      filter.isOutOfService = true;
    }

    return filter;
  }

  private mapSortOrderToFilterSort(sortOrder: PumpSortOrder): {
    sortBy: DeviceSortBy;
    sortDir: DeviceSortDir;
  } {
    switch (sortOrder) {
      case 'nameDesc':
        return { sortBy: 'name', sortDir: 'desc' };
      case 'lastSeenDateAsc':
        return { sortBy: 'lastSeenDate', sortDir: 'asc' };
      case 'lastSeenDateDesc':
        return { sortBy: 'lastSeenDate', sortDir: 'desc' };
      case 'nameAsc':
      default:
        return { sortBy: 'name', sortDir: 'asc' };
    }
  }

  private mapFilterToSortOrder(
    sortBy: DeviceSortBy,
    sortDir: DeviceSortDir,
  ): PumpSortOrder {
    if (sortBy === 'lastSeenDate' && sortDir === 'asc') {
      return 'lastSeenDateAsc';
    }

    if (sortBy === 'lastSeenDate' && sortDir === 'desc') {
      return 'lastSeenDateDesc';
    }

    if (sortBy === 'name' && sortDir === 'desc') {
      return 'nameDesc';
    }

    return 'nameAsc';
  }
}
